import {
  Card,
  CardFooter,
  CardHeader,
  DialogContent,
  FormSection,
  NextAlert,
  NextDialog,
  TextInput,
} from '@cint/wonderment';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { forwardRef, useImperativeHandle, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { usePostData } from '@/api/apiHooks';
import { URLS } from '@/utils/urls';

interface ITremendousFormInput {
  apiKey: string;
}

export interface TremendousDialogRef {
  open: () => void;
  close: () => void;
}

export interface TremendousDialogProps {
  onSubmit?: (data: ITremendousFormInput) => void | Promise<void>;
  companyId: string;
  title: string;
  alert: {
    description: string;
    variant: 'info' | 'warning';
    iconName: string;
    buttonText?: string;
  };
}

export const TremendousDialog = forwardRef<
  TremendousDialogRef,
  TremendousDialogProps
>(function TremendousDialogWithoutRef(
  { onSubmit: onSubmitProp, companyId, alert, title },
  ref
) {
  const [isVisible, setIsVisible] = useState(false);
  const [step, setStep] = useState(0);
  const queryClient = useQueryClient();
  const { mutate } = usePostData({
    url: URLS.updateCompanySettings(companyId),
    successMessage: 'Tremendous credentials updated successfully',
    method: 'PUT',
    etagSourceUrl: URLS.getCompanyById(companyId),
    showErrorToast: false,
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch,
    setError,
  } = useForm<ITremendousFormInput>({
    mode: 'onBlur',
    defaultValues: {
      apiKey: '',
    },
  });

  const [apiKey] = watch(['apiKey']);

  const handleClose = () => {
    setIsVisible(false);
    setStep(0);
    reset();
  };

  function onSubmit(data: ITremendousFormInput) {
    mutate(
      {
        tremendous_settings: {
          api_key: data.apiKey,
        },
      },
      {
        onSuccess: () => {
          if (onSubmitProp) {
            onSubmitProp(data);
          }
          queryClient.invalidateQueries({
            queryKey: [URLS.getCompanyById(companyId)],
          });
          handleClose();
        },
        onError: (
          error: AxiosError<{
            errors?: { api_key?: Array<{ message: string }> };
          }>
        ) => {
          const message = error.response?.data?.errors?.api_key?.[0]?.message;
          if (message) {
            setError('apiKey', { message });
          }
        },
      }
    );
  }

  useImperativeHandle(ref, () => ({
    open: () => setIsVisible(true),
    close: handleClose,
  }));

  return (
    <NextDialog open={isVisible}>
      <DialogContent>
        <Card variant="subtle">
          <CardHeader title={title} />
          {step === 0 ? (
            <NextAlert {...alert} title="Global impact action" />
          ) : (
            <FormSection size="base" variant="vertical">
              <form onSubmit={handleSubmit(onSubmit)}>
                <Controller
                  name="apiKey"
                  control={control}
                  rules={{
                    required: 'API key is required',
                  }}
                  render={({ field }) => (
                    <TextInput
                      {...field}
                      labelProps={{
                        label: 'API key',
                        tooltipIconProps: {
                          variant: 'info',
                        },
                        tooltipContent:
                          'This is a secure key that connects your Engage panel to your Tremendous account so you can fund and send rewards to your panelists. Please ensure you copy an API Key from your Tremendous developer settings.',
                      }}
                      isRequired
                      hasError={!!errors.apiKey}
                      decoratorTextProps={{
                        description: errors.apiKey?.message,
                      }}
                    />
                  )}
                />
              </form>
            </FormSection>
          )}
          <CardFooter
            primaryButtonProps={{
              text:
                step === 0
                  ? alert.buttonText ?? 'Continue'
                  : 'Test connection and link account',
              onClick: () => {
                if (step === 0) {
                  setStep(1);
                } else {
                  handleSubmit(onSubmit)();
                }
              },
              disabled: step === 1 && (isSubmitting || !apiKey),
            }}
            secondaryButtonProps={{
              text: 'Cancel',
              onClick: handleClose,
              disabled: isSubmitting,
            }}
          />
        </Card>
      </DialogContent>
    </NextDialog>
  );
});
